import { escapeHTML } from '../../utils/escape.js';
import { formatMonto } from '../../utils/formatters.js';

/**
 * Cuerpo del cajón "Adelanto" (S4, design.md AD7, spec `payroll-advances`).
 * Función de render pura — escapa TODOS sus propios valores interpolados
 * (AD7: el shell sólo escapa el título). Alta + lista activa/anulada; la
 * anulación de un adelanto ya consumido por una liquidación se refleja como
 * el `error` que el server devuelve (`adelanto_liquidado`), nunca bloqueada
 * acá de antemano — este cuerpo no sabe qué períodos están liquidados.
 *
 * `fecha` de la columna `date` de Postgres — se muestra con el mismo
 * formateador string-only que el resto del tablero (nunca `Date`/`Intl`,
 * mismo motivo que `liquidacion-comprobante-policy.js#formatFechaPeriodo`).
 * @param {string|null|undefined} fechaSql
 * @returns {string}
 */
function formatFechaSql(fechaSql) {
    if (typeof fechaSql !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(fechaSql)) return '—';
    const [anio, mes, dia] = fechaSql.split('-');
    return `${dia}/${mes}/${anio}`;
}

const BTN = 'px-3 py-2 rounded-xl bg-brand text-white text-[11px] font-black uppercase tracking-widest shadow disabled:opacity-60 disabled:pointer-events-none';
const BTN_SECONDARY = 'px-3 py-1.5 rounded-xl border border-border-subtle text-text-muted text-[10px] font-black uppercase tracking-widest hover:text-text-primary';
const INPUT = 'w-full bg-base border border-border-subtle rounded-xl px-3 py-2 text-sm text-text-primary focus:outline-none focus:border-brand';

/**
 * @param {{
 *   empleadoNombre: string,
 *   fecha?: string, monto?: string, nota?: string,
 *   cargando: boolean,
 *   activos: Array<{id:string, fecha:string, monto:number, nota:string|null}>,
 *   anulados: Array<{id:string, fecha:string, monto:number, nota:string|null, anulado_motivo:string|null}>,
 *   error?: string|null,
 *   submitting?: boolean,
 *   anulandoId?: string|null,
 *   motivo?: string,
 *   anularError?: string|null,
 *   incierto?: boolean,
 *   mensaje?: string|null,
 * }} model
 * @returns {string}
 */
export function AdelantoDrawerView(model = {}) {
    const activos = model.activos ?? [];
    const anulados = model.anulados ?? [];

    const filaActiva = (a) => `
        <li class="flex items-center justify-between gap-2 py-2 border-b border-border-subtle last:border-0" data-adelanto-activo data-id="${escapeHTML(a.id)}">
            <span class="text-xs text-text-primary">${formatFechaSql(a.fecha)} · ${formatMonto(a.monto)}${a.nota ? ` · ${escapeHTML(a.nota)}` : ''}</span>
            ${
                model.anulandoId === a.id
                    ? `
                <form data-action-form="adelanto-anular-confirmar" data-id="${escapeHTML(a.id)}" class="flex items-center gap-2">
                    <input type="text" name="motivo" value="${escapeHTML(model.motivo ?? '')}" required class="${INPUT}" placeholder="Motivo" />
                    <button type="submit" class="${BTN_SECONDARY}">Confirmar</button>
                    <button type="button" data-action="adelanto-anular-cancelar" class="${BTN_SECONDARY}">Cancelar</button>
                </form>
            `
                    : `<button type="button" data-action="adelanto-anular-abrir" data-id="${escapeHTML(a.id)}" class="${BTN_SECONDARY}">Anular</button>`
            }
        </li>
    `;

    const filaAnulada = (a) => `
        <li class="py-2 border-b border-border-subtle last:border-0 text-xs text-text-muted line-through" data-adelanto-anulado data-id="${escapeHTML(a.id)}">
            ${formatFechaSql(a.fecha)} · ${formatMonto(a.monto)} — ${escapeHTML(a.anulado_motivo ?? '')}
        </li>
    `;

    return `
        <div data-drawer-tipo="adelanto">
            <p class="text-xs font-bold text-text-muted mb-4">${escapeHTML(model.empleadoNombre ?? '')}</p>

            ${model.error ? `<div role="alert" class="mb-3 px-3 py-2 rounded-xl bg-red-50 border border-red-200 text-red-800 text-xs font-bold">${escapeHTML(model.error)}</div>` : ''}
            ${model.anularError ? `<div role="alert" class="mb-3 px-3 py-2 rounded-xl bg-red-50 border border-red-200 text-red-800 text-xs font-bold">${escapeHTML(model.anularError)}</div>` : ''}

            ${
                model.incierto
                    ? `
            <div role="alert" class="mb-6 px-3 py-3 rounded-xl bg-amber-50 border border-amber-300 text-amber-900 text-xs font-bold" data-adelanto-incierto>
                <p class="mb-2">${escapeHTML(model.mensaje ?? '')}</p>
                <button type="button" data-action="tablero-adelanto-actualizar" class="${BTN}">Actualizar lista</button>
            </div>
            `
                    : `
            <form data-action-form="adelanto-crear" class="space-y-3 mb-6">
                <label class="block text-[11px] font-black uppercase tracking-widest text-text-muted">
                    Fecha
                    <input type="date" name="fecha" value="${escapeHTML(model.fecha ?? '')}" required class="${INPUT} mt-1" />
                </label>
                <label class="block text-[11px] font-black uppercase tracking-widest text-text-muted">
                    Monto
                    <input type="number" name="monto" step="0.01" min="0.01" value="${escapeHTML(model.monto ?? '')}" required class="${INPUT} mt-1" />
                </label>
                <label class="block text-[11px] font-black uppercase tracking-widest text-text-muted">
                    Nota (opcional)
                    <input type="text" name="nota" value="${escapeHTML(model.nota ?? '')}" class="${INPUT} mt-1" />
                </label>
                <button type="submit" class="${BTN}" ${model.submitting ? 'disabled' : ''}>Registrar adelanto</button>
            </form>
            `
            }

            <div>
                <h3 class="text-[11px] font-black uppercase tracking-widest text-text-muted mb-2">Adelantos activos</h3>
                ${model.cargando ? '<p class="text-xs text-text-muted">Cargando...</p>' : activos.length ? `<ul>${activos.map(filaActiva).join('')}</ul>` : '<p class="text-xs text-text-muted">Sin adelantos activos.</p>'}
            </div>

            ${
                anulados.length
                    ? `
                <div class="mt-4">
                    <h3 class="text-[11px] font-black uppercase tracking-widest text-text-muted mb-2">Anulados</h3>
                    <ul>${anulados.map(filaAnulada).join('')}</ul>
                </div>
            `
                    : ''
            }
        </div>
    `;
}

